<script setup>
import { computed, ref, watch } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { getContactChannelHandle } from 'dashboard/helper/inbox';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './CardLabelsV5.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
import UnreadBadge from './UnreadBadge.vue';
import SLACardLabel from 'dashboard/components-next/Conversation/Sla/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);

const unreadCount = computed(() => props.chat.unread_count);

// Who this contact is on this channel - their number over SMS, their address
// over email. Blank when it would only repeat the name above it.
const contactHandle = computed(() =>
  getContactChannelHandle(props.currentContact, props.inbox)
);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const isAgentBotAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'AgentBot'
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11';
});

const onThumbnailHover = () => {
  hovered.value = true;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-center gap-2.5 flex-grow-0 flex-shrink-0 w-auto max-w-full px-3 py-2.5 transition-colors cursor-pointer conversation rounded-xl border-b border-n-weak/70 hover:border-transparent group hover:z-[1] after:pointer-events-none after:absolute after:inset-y-1.5 after:w-0.5 after:rounded-full ltr:after:left-0 rtl:after:right-0"
    :class="{
      'active animate-card-select bg-n-violet-2 !border-transparent after:bg-n-brand':
        isActiveChat,
      selected: selected,
      'bg-n-slate-2 !border-transparent': selected && !isActiveChat,
      'hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3': !isActiveChat && !selected,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      v-tooltip.bottom="inbox.name"
      class="relative flex-shrink-0"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :inbox="inbox"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 min-w-0">
        <h4
          class="conversation--user text-sm my-0 capitalize truncate flex-1 min-w-0 text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <span
          v-if="showAssignee && assignee.name"
          class="text-n-slate-10 text-xs inline-flex items-center gap-px max-w-24 flex-shrink-0"
        >
          <Icon
            :icon="isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'"
            class="size-3 flex-shrink-0"
          />
          <span class="truncate">{{ assignee.name }}</span>
        </span>
        <CardPriorityIcon
          :priority="chat.priority"
          class="flex-shrink-0 !size-3.5"
        />
        <TimeAgo
          compact
          class="flex-shrink-0"
          :last-activity-timestamp="chat.timestamp"
          :created-at-timestamp="chat.created_at"
          :conversation-id="chat.id"
        />
      </div>
      <p
        v-if="contactHandle"
        :title="contactHandle"
        class="my-0 text-xs leading-4 truncate text-n-slate-10"
      >
        {{ contactHandle }}
      </p>
      <div class="flex items-center gap-1.5 min-w-0 mt-0.5">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          class="flex-1 min-w-0 h-5"
          :class="messagePreviewClass"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="flex-1 min-w-0 text-sm leading-5 h-5"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="text-n-slate-11 text-sm my-0 leading-5 h-5 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          :class="messagePreviewClass"
        >
          <fluent-icon
            size="16"
            class="-mt-0.5 align-middle inline-block text-n-slate-10"
            icon="info"
          />
          <span class="mx-0.5">
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="flex-shrink-0"
        />
      </div>
      <CardLabels v-if="showLabelsSection" :labels="chat.labels" class="mt-1">
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" data-before-slot />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
